import React, { useCallback, useContext, useMemo } from "react";
import {
	Card,
	CardHeader,
	CardContent,
	CardActions,
	Collapse,
	IconButton,
	Typography,
	Badge,
	Checkbox,
	Box,
	Stack,
	Button,
	useTheme,
} from "@mui/material";
import {
	ExpandMore as ExpandMoreIcon,
	ExpandLess as ExpandLessIcon,
	Delete as DeleteIcon,
	ContentCopy as DuplicateIcon,
	Error as ErrorIcon,
} from "@mui/icons-material";
import { JobCardProps, countValidationErrors } from "./JobCard.types";
import type { Branch, Job } from "../../../../../../models";
import { isFieldVisible, ValidationContext } from "../../../../../../utilities/jobValidation";
import { ImportTableColumn } from "../../../../../../utilities/types";
import { ColumnContext } from "../../columns/standard-column-types";

// Mobile column components
import { TextColumnMobile } from "../../columns/standard-column-types/textColumn/textColumn.mobile";
import { SelectColumnMobile } from "../../columns/standard-column-types/selectColumn/selectColumn.mobile";
import { CheckboxColumnMobile } from "../../columns/standard-column-types/checkboxColumn/checkboxColumn.mobile";
import { DateColumnMobile } from "../../columns/standard-column-types/dateColumn/dateColumn.mobile";
import { GeoJSONFileColumnMobile } from "../../columns/standard-column-types/geoJSONFileColumn/geoJSONFileColumn.mobile";
import { DealColumnMobile } from "../../columns/Deal.column/Deal.column.mobile";
import { SalespersonColumnMobile } from "../../columns/Salesperson.column/Salesperson.column.mobile";
import { SalespersonPhoneColumnMobile } from "../../columns/SalespersonPhone.column/SalespersonPhone.column.mobile";
import { LastCropColumnMobile } from "../../columns/LastCrop.column/LastCrop.column.mobile";
import { NextCropColumnMobile } from "../../columns/NextCrop.column/NextCrop.column.mobile";
import { FieldNotesColumnMobile } from "../../columns/FieldNotes.column/FieldNotes.column.mobile";
import { getJobPriorities, JobsPriority } from "../../../../../../utilities/types";
import JobMap from "../../../../../helpers/JobMap";
import { UserContext } from "../../../../../../hooks/UserContext";

/**
 * Get the validation context for a job to check field visibility
 */
function getJobValidationContext(job: Job, columnContext: ColumnContext): ValidationContext {
	const { props } = columnContext;
	// Find the deal for this job
	const dealId = job.dealIds?.[0];
	const deal = dealId ? props.allDeals?.find((d) => d.id === dealId) : props.deals?.[0];

	return {
		company: props.selectedCompany,
		user: props.user,
		deal,
		job,
		integration: props.integration,
		hasPointsFile: !!job.files?.pointsGeoJSON?.features?.length,
	};
}

/**
 * Check if a field should be visible based on company settings and business logic
 */
function shouldShowField(columnId: ImportTableColumn, context: ValidationContext): boolean {
	return isFieldVisible(columnId, context);
}

/**
 * JobCard component for mobile view of ImportTable.
 * Displays a single job as an expandable card with editable fields.
 * Uses the same mobile column components as desktop for consistency.
 */
export const JobCard: React.FC<JobCardProps> = ({
	job,
	validationErrors,
	columnContext,
	expanded,
	onToggle,
	onDelete,
	onDuplicate,
	selected,
	onSelectToggle,
}) => {
	const theme = useTheme();
	const currentUser = useContext(UserContext);
	const isSuperUser = currentUser?.isSuperUser ?? false;
	const errorCount = countValidationErrors(validationErrors, job.id);
	const hasErrors = errorCount > 0;

	const onGeometryEdit = useCallback(
		(_jobId: string, jsonKey: string, updated: unknown) => {
			columnContext.setJobs((prev) =>
				prev.map((j) => (j.id === job.id ? { ...j, files: { ...j.files, [jsonKey]: updated } } : j)),
			);
		},
		[columnContext.setJobs, job.id],
	);

	// Get validation context for visibility checks
	const visibilityContext = useMemo(() => getJobValidationContext(job, columnContext), [job, columnContext]);

	// Helper to check if a field should be shown
	const isVisible = (columnId: ImportTableColumn) => shouldShowField(columnId, visibilityContext);

	return (
		<Card
			data-testid="job-card"
			sx={{
				mb: 2,
				borderLeft: hasErrors ? `4px solid ${theme.palette.error.main}` : undefined,
			}}
		>
			<CardHeader
				data-testid="job-card-header"
				onClick={() => onToggle(job.id)}
				sx={{
					cursor: "pointer",
					py: 1,
					"&:hover": { backgroundColor: "action.hover" },
				}}
				avatar={
					<Checkbox
						checked={selected}
						onClick={(e) => {
							e.stopPropagation();
							onSelectToggle(job.id);
						}}
						size="medium"
						sx={{ p: 0 }}
					/>
				}
				title={
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Typography variant="subtitle1" fontWeight="bold">
							{job.importGrowerName || "No Grower"}
						</Typography>
						{hasErrors && (
							<Badge data-testid="error-badge" badgeContent={errorCount} color="error" sx={{ ml: 1 }}>
								<ErrorIcon color="error" fontSize="small" />
							</Badge>
						)}
					</Box>
				}
				subheader={
					<Typography variant="body2" color="text.secondary">
						{job.importFieldName || "No Field Name"}
						{job.importFarmName && ` • ${job.importFarmName}`}
					</Typography>
				}
				action={<IconButton size="small">{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>}
			/>

			<Collapse in={expanded} timeout="auto" unmountOnExit>
				<CardContent data-testid="job-card-content" sx={{ pt: 0 }}>
					<br />
					<Stack spacing={2}>
						{/*  ==========================================================================================
						// 	IMPORT_FIELDS: If you are adding a new column, please also add it to all other places with this comment.
						// Maintain the same order in all places for consistency.
						// ========================================================================================== */}

						{/* Basic Information */}
						{isVisible("grower") && (
							<TextColumnMobile
								job={job}
								columnContext={columnContext}
								fieldName="importGrowerName"
								columnId="grower"
								required
							/>
						)}
						{isVisible("farm") && (
							<TextColumnMobile job={job} columnContext={columnContext} fieldName="importFarmName" columnId="farm" />
						)}
						{isVisible("field") && (
							<TextColumnMobile
								job={job}
								columnContext={columnContext}
								fieldName="importFieldName"
								columnId="field"
								required
							/>
						)}

						{/* Attachments */}
						{isVisible("boundaryGeoJSON") && (
							<GeoJSONFileColumnMobile
								job={job}
								columnContext={columnContext}
								optionsKey="boundaryOptions"
								geoJSONKey="boundaryGeoJSON"
								columnId="boundaryGeoJSON"
							/>
						)}
						{isVisible("pointsGeoJSON") && (
							<GeoJSONFileColumnMobile
								job={job}
								columnContext={columnContext}
								optionsKey="pointsOptions"
								geoJSONKey="pointsGeoJSON"
								columnId="pointsGeoJSON"
							/>
						)}
						{isVisible("zonesGeoJSON") && (
							<GeoJSONFileColumnMobile
								job={job}
								columnContext={columnContext}
								optionsKey="zonesOptions"
								geoJSONKey="zonesGeoJSON"
								columnId="zonesGeoJSON"
							/>
						)}
						{isVisible("fieldNotes") && <FieldNotesColumnMobile job={job} columnContext={columnContext} />}

						{/* Order */}
						{isVisible("isReadyToSample") && (
							<CheckboxColumnMobile
								job={job}
								columnContext={columnContext}
								fieldName="isReadyToSample"
								columnId="isReadyToSample"
							/>
						)}
						{isVisible("dateFieldReadyEstimated") && (
							<DateColumnMobile
								job={job}
								columnContext={columnContext}
								fieldName="dateFieldReadyEstimated"
								columnId="dateFieldReadyEstimated"
							/>
						)}
						{isVisible("fieldPriority") && (
							<SelectColumnMobile<string>
								job={job}
								columnContext={columnContext}
								fieldName="fieldPriority"
								columnId="fieldPriority"
								options={[...getJobPriorities()]}
								freeSolo={false}
								getValue={(j) => j.fieldPriority || "Regular Turn (default)"}
								setValue={(j, value) => {
									j.fieldPriority = (value || "Regular Turn (default)") as JobsPriority;
								}}
							/>
						)}
						{isVisible("collectBoundary") && (
							<CheckboxColumnMobile
								job={job}
								columnContext={columnContext}
								fieldName="collectBoundary"
								columnId="collectBoundary"
							/>
						)}
						{isVisible("submissionNotes") && (
							<TextColumnMobile
								job={job}
								columnContext={columnContext}
								fieldName="submissionNotes"
								columnId="submissionNotes"
								multiline
								rows={3}
							/>
						)}

						{/* Deal */}
						{isVisible("density") && (
							<SelectColumnMobile
								job={job}
								columnContext={columnContext}
								fieldName="importDensity"
								columnId="density"
								options={columnContext.gridSizes}
								isNumber
								required
							/>
						)}
						{isVisible("depth") && (
							<SelectColumnMobile
								job={job}
								columnContext={columnContext}
								fieldName="importDepth"
								columnId="depth"
								options={columnContext.depths}
								isNumber
								required
							/>
						)}
						{isVisible("labName") && (
							<TextColumnMobile job={job} columnContext={columnContext} fieldName="importLabName" columnId="labName" />
						)}
						{isVisible("testPackage") && (
							<SelectColumnMobile
								job={job}
								columnContext={columnContext}
								fieldName="externalTestPackageId"
								columnId="testPackage"
								options={columnContext.labPackages}
							/>
						)}
						{isVisible("frequencyOfMicros") && (
							<SelectColumnMobile
								job={job}
								columnContext={columnContext}
								fieldName="externalFrequencyOfMicros"
								columnId="frequencyOfMicros"
								options={columnContext.micros}
								isNumber
							/>
						)}
						{isVisible("deal") && <DealColumnMobile job={job} columnContext={columnContext} required />}

						{/* Lab Info */}
						{isVisible("labInstructions") && (
							<TextColumnMobile
								job={job}
								columnContext={columnContext}
								fieldName="labInstructions"
								columnId="labInstructions"
								multiline
								rows={2}
							/>
						)}
						{isVisible("microsList") && (
							<TextColumnMobile
								job={job}
								columnContext={columnContext}
								fieldName="microsList"
								columnId="microsList"
								disabled={!job?.files?.pointsGeoJSON?.features?.length}
							/>
						)}
						{isVisible("lastCrop") && <LastCropColumnMobile job={job} columnContext={columnContext} />}
						{isVisible("nextCrop") && <NextCropColumnMobile job={job} columnContext={columnContext} />}
						{isVisible("yieldGoal") && (
							<TextColumnMobile job={job} columnContext={columnContext} fieldName="yieldGoal" columnId="yieldGoal" />
						)}

						{/* Contacts */}
						{isVisible("branch") && (
							<SelectColumnMobile<Branch>
								job={job}
								columnContext={columnContext}
								fieldName="branchIds"
								columnId="branch"
								options={columnContext.props.branches || []}
								freeSolo={false}
								getOptionLabel={(branch) => branch.name}
								isOptionEqualToValue={(option, value) => option.id === value.id}
								getValue={(j) =>
									(columnContext.props.branches || []).find((branch) => j.branchIds?.includes(branch.id)) || null
								}
								setValue={(j, branch) => {
									j.branchIds = branch ? [branch.id] : [];
								}}
							/>
						)}
						{isVisible("salesperson") && <SalespersonColumnMobile job={job} columnContext={columnContext} />}
						{isVisible("salespersonMobilePhone") && (
							<SalespersonPhoneColumnMobile job={job} columnContext={columnContext} />
						)}
						{isVisible("growerPhoneNumber") && (
							<TextColumnMobile
								job={job}
								columnContext={columnContext}
								fieldName="growerPhoneNumber"
								columnId="growerPhoneNumber"
								inputType="tel"
							/>
						)}
						{isVisible("submitterName") && (
							<TextColumnMobile
								job={job}
								columnContext={columnContext}
								fieldName="submitterName"
								columnId="submitterName"
							/>
						)}
						{isVisible("submitterEmail") && (
							<TextColumnMobile
								job={job}
								columnContext={columnContext}
								fieldName="submitterEmail"
								columnId="submitterEmail"
								inputType="email"
							/>
						)}

						{/* Sync Codes */}
						{isVisible("eventId") && (
							<TextColumnMobile job={job} columnContext={columnContext} fieldName="eventId" columnId="eventId" />
						)}
						{isVisible("labSubmissionCode") && (
							<TextColumnMobile
								job={job}
								columnContext={columnContext}
								fieldName="labSubmissionCode"
								columnId="labSubmissionCode"
							/>
						)}
						{isVisible("fieldId") && (
							<TextColumnMobile job={job} columnContext={columnContext} fieldName="fieldId" columnId="fieldId" />
						)}
						{isVisible("externalId") && (
							<TextColumnMobile job={job} columnContext={columnContext} fieldName="externalId" columnId="externalId" />
						)}
						{isVisible("externalFarmId") && (
							<TextColumnMobile
								job={job}
								columnContext={columnContext}
								fieldName="externalFarmId"
								columnId="externalFarmId"
							/>
						)}
						{isVisible("externalLabId") && (
							<TextColumnMobile
								job={job}
								columnContext={columnContext}
								fieldName="externalLabId"
								columnId="externalLabId"
							/>
						)}

						{/* Misc */}
						{isVisible("isTestJob") && (
							<CheckboxColumnMobile
								job={job}
								columnContext={columnContext}
								fieldName="isTestJob"
								columnId="isTestJob"
							/>
						)}

						{/* Map Preview */}
						<JobMap
							job={job}
							height={400}
							colorMode="layer-type"
							showEmptyState
							disableDefaultUI
							zoomControl
							boundsPadding={{ top: 20, right: 20, bottom: 20, left: 20 }}
							onGeometryEdit={isSuperUser ? onGeometryEdit : undefined}
						/>
					</Stack>
				</CardContent>

				<CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
					<Button startIcon={<DuplicateIcon />} onClick={() => onDuplicate(job.id)} size="small">
						Duplicate
					</Button>
					<Button startIcon={<DeleteIcon />} onClick={() => onDelete(job.id)} color="error" size="small">
						Delete
					</Button>
				</CardActions>
			</Collapse>
		</Card>
	);
};
